import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import {
  PlusCircle,
  ChevronUp,
} from "feather-icons-react/build/IconComponents";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { setToogleHeader } from "../../core/redux/action";
import { useToast } from "../../Toaster/ToastContext";
import Loader from "../loader/loader";
import withReactContent from "sweetalert2-react-content";
import { apiClient } from "../../services/apiClient";
import Table from "../../core/pagination/datatable";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";

const ExpenseCategory = () => {
  const dispatch = useDispatch();
  const data = useSelector((state) => state.toggle_header);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [expensecategory, setExpensecategory] = useState([]);
  const [filteredExpcategories, setFilteredExpcategories] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editCategoryId, setEditCategoryId] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const { showToast } = useToast();
  const [permissions, setPermissions] = useState({
    create: false,
    read: false,
    update: false,
    delete: false,
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalPages: 0,
    totalCount: 0,
  });
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    status: "active",
  });
  const [errors, setErrors] = useState({
    name: "",
    status: "",
  });
  const [loading, setLoading] = useState(false);
  const hasFetched = useRef(false);
  const MySwal = withReactContent(Swal);

  const handleAdd = () => {
    setFormData({ name: "", description: "", status: "active" });
    setEditCategoryId(null);
    setIsEditMode(false);
    setIsModalOpen(true);
  };

  const handleEdit = (category) => {
    setFormData({
      name: category.name,
      description: category.description,
      status: category.status,
    });
    setEditCategoryId(category.id);
    setIsEditMode(true);
    setIsModalOpen(true);
  };

  useEffect(() => {
    if (isModalOpen) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }
  }, [isModalOpen]);

  const renderCollapseTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>
      Collapse
    </Tooltip>
  );

  const validateField = (name, value) => {
    switch (name) {
      case "name":
        return value.trim() ? "" : "Expense category name is required";
      case "status":
        return value ? "" : "Status is required";
      default:
        return "";
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  const validateForm = () => {
    const newErrors = {
      name: validateField("name", formData.name),
      status: validateField("status", formData.status),
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error !== "");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setLoading(true);
      try {
        const response = apiClient.post("/store-update-expense-category", {
          ...formData,
          ...(isEditMode && { id: editCategoryId }),
        });
        const Apidata = await response;
        if (Apidata.status) {
          setFormData({ name: "", description: "", status: "active" });
          setErrors({ name: "", value: "", status: "" });
          setIsModalOpen(false);
          setEditCategoryId(null);
          setIsEditMode(false);
          showToast(Apidata.message, "success", "Expense Category Operation!");
          hasFetched.current = false;
          await fetchExpenseCategories();
        }
      } catch (error) {
        showToast(
          error.message ||
            "An unexpected error occurred: Please contact the support team.",
          "error",
          "Expense Category Operation!"
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const fetchExpenseCategories = async (currentPage = 1, pageSize = 20) => {
    if (hasFetched.current) return;
    setLoading(true);
    const pageIndex = currentPage - 1;
    try {
      const response = await apiClient.post("/paginate-expense-category", {
        pageIndex,
        pageSize,
      });
      const data = await response;
      if (data.success) {
        setExpensecategory(data.data);
        setFilteredExpcategories(data.data);
        setPagination({
          currentPage: data.currentPage ?? currentPage,
          pageSize: data.pageSize ?? pageSize,
          totalPages: data.totalPages ?? 1,
          totalCount: data.totalCount || data.data.length,
        });
      } else {
        setExpensecategory([]);
        setFilteredExpcategories([]);
      }
    } catch (error) {
      showToast(
        error.message || "Failed to fetch expense categories",
        "error",
        "Expense Categories"
      );
    } finally {
      setLoading(false);
      hasFetched.current = true;
    }
  };

  useEffect(() => {
    if (!hasFetched.current) {
      fetchExpenseCategories(pagination.currentPage, pagination.pageSize);
    }
  }, [pagination.currentPage, pagination.pageSize]);

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }));
    hasFetched.current = false;
  }, []);

  useEffect(() => {
    fetchExpenseCategories(); // load categories from API

    const storedPermissions = localStorage.getItem("rolespermissions");
    try {
      if (!storedPermissions) return;

      const Permissions = JSON.parse(storedPermissions);
      const getPermissions = JSON.parse(Permissions.permissions);

      let parsedPermissions = getPermissions.permissions || getPermissions;

      // 🔹 Find Finance & Accounts section
      const financeSection = parsedPermissions.find(
        (item) => item.title === "Finance & Accounts"
      );

      // 🔹 Find Expenses parent
      const expensesSection = financeSection?.childPage?.find(
        (child) => child.title === "Expenses"
      );

      // 🔹 Find Expense Category page inside Expenses
      const expenseCategoryPage = expensesSection?.childPage?.find(
        (child) => child.title === "Expense Category"
      );

      if (expenseCategoryPage) {
        setPermissions({
          create: expenseCategoryPage.create || false,
          read: expenseCategoryPage.read || false,
          update: expenseCategoryPage.update || false,
          delete: expenseCategoryPage.delete || false,
        });
      } else {
        showToast(
          "Expense Category permissions not found.",
          "error",
          "Permissions Error"
        );
      }
    } catch (error) {
      console.error("Error parsing permissions from localStorage:", error);
      showToast("Failed to load permissions.", "error", "Permissions Error");
    }
  }, []);

  const handleSearchExpenseCategory = (e) => {
    const term = e.target.value.trim().toLowerCase();
    if (!term) {
      setFilteredExpcategories([]);
      setIsSearching(false);
      return;
    }
    const filtered = expensecategory.filter(
      (category) =>
        category.name?.toLowerCase().includes(term) ||
        category.description?.toLowerCase().includes(term)
    );
    setFilteredExpcategories(filtered);
    setIsSearching(true);
  };

  const handleToggleStatus = async (category) => {
    const newStatus = category.status === "active" ? "inactive" : "active";
    try {
      setLoading(true);
      const response = await apiClient.post("/store-update-expense-category", {
        id: category.id,
        status: newStatus,
        name: category.name,
        description: category.description,
      });
      if (response.status) {
        showToast(
          `Expense category status updated to ${newStatus}`,
          "success",
          "Expense Category Operation"
        );
        setExpensecategory((prev) =>
          prev.map((c) =>
            c.id === category.id ? { ...c, status: newStatus } : c
          )
        );
        setFilteredExpcategories((prev) =>
          prev.map((c) =>
            c.id === category.id ? { ...c, status: newStatus } : c
          )
        );
      } else {
        showToast(
          response.message || "Failed to update expense category status",
          "error",
          "Expense Category Operation"
        );
      }
    } catch (error) {
      showToast(
        error.message || "Failed to update expense category status",
        "error",
        "Expense Category Operation"
      );
    } finally {
      setLoading(false);
    }
  };

  const showConfirmationAlert = (record) => {
    MySwal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      showCancelButton: true,
      confirmButtonColor: "#00ff00",
      confirmButtonText: "Yes, delete it!",
      cancelButtonColor: "#ff0000",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setLoading(true);
          const response = await apiClient.post("/delete-expense-category", {
            id: record.id,
          });
          const data = await response;
          if (data.status) {
            showToast(
              "Expense category deleted successfully",
              "success",
              "Expense Category Operation!"
            );
            hasFetched.current = false;
            await fetchExpenseCategories(
              pagination.currentPage,
              pagination.pageSize
            );
          } else {
            showToast(
              data.message || "Failed to delete expense category",
              "error",
              "Expense Category Operation!"
            );
          }
        } catch (error) {
          showToast(
            error.message || "Failed to delete expense category",
            "error",
            "Expense Category Operation!"
          );
        } finally {
          setLoading(false);
        }
      } else {
        MySwal.close();
      }
    });
  };

  const columns = [
    {
      title: "Category Name",
      dataIndex: "name",
      render: (text) => <span className="wrap-text">{text}</span>,
      sorter: (a, b) => a.name.length - b.name.length,
      width: 200,
    },
    {
      title: "Description",
      dataIndex: "description",
      render: (text) => <span className="wrap-text">{text}</span>,
      sorter: (a, b) => a.description.length - b.description.length,
      width: 200,
    },

    // ✅ Only show Status column if read permission is true
    ...(permissions.read
      ? [
          {
            title: "Status",
            dataIndex: "status",
            render: (text, record) => (
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={text.toLowerCase() === "active"}
                  onChange={() => handleToggleStatus(record)}
                />
                <span className="toggle-slider"></span>
              </label>
            ),
            sorter: (a, b) => a.status.length - b.status.length,
            width: 100,
          },
        ]
      : []),

    // Actions column: only if read OR delete permission
    ...(permissions.read || permissions.delete
      ? [
          {
            title: "Actions",
            dataIndex: "actions",
            key: "actions",
            render: (_, record) => (
              <td className="action-table-data">
                <div className="edit-delete-action">
                  {permissions.read && (
                    <Link
                      className="me-2 p-2"
                      to="#"
                      onClick={() => handleEdit(record)}
                    >
                      <i data-feather="edit" className="feather-edit"></i>
                    </Link>
                  )}
                  {permissions.delete && (
                    <Link
                      className="confirm-text p-2"
                      to="#"
                      onClick={() => showConfirmationAlert(record)}
                    >
                      <i data-feather="trash-2" className="feather-trash-2"></i>
                    </Link>
                  )}
                </div>
              </td>
            ),
            width: 150,
          },
        ]
      : []),
  ];

  const displayCategories = isSearching
    ? filteredExpcategories
    : expensecategory;

  const { currentPage, pageSize, totalPages, totalCount } = pagination;
  const startItem = totalCount > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endItem = Math.min(startItem + pageSize - 1, totalCount);

  const pagesToShow = [];
  if (totalPages > 0) {
    if (totalPages <= 2) {
      for (let i = 1; i <= totalPages; i++) {
        pagesToShow.push(i);
      }
    } else {
      let middle = [];
      if (currentPage === 1) {
        middle = [1, 2];
      } else if (currentPage === totalPages) {
        middle = [totalPages - 1, totalPages];
      } else {
        middle = [currentPage, currentPage + 1];
      }

      if (middle[0] > 1) {
        pagesToShow.push(1);
      }
      if (middle[0] > 2) {
        pagesToShow.push("...");
      }
      middle.forEach((p) => pagesToShow.push(p));
      if (middle[1] < totalPages - 1) {
        pagesToShow.push("...");
      }
      if (middle[1] < totalPages) {
        pagesToShow.push(totalPages);
      }
    }
  }

  const changePage = (page) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: page,
    }));
    hasFetched.current = false;
  };

  return (
    <>
      <div>
        <Loader loading={loading} />
        <div
          className="page-wrapper"
          style={{ position: "relative", minHeight: "100vh" }}
        >
          <div className="content" style={{ opacity: loading ? 0.5 : 1 }}>
            <div className="page-header">
              <div className="add-item d-flex">
                <div className="page-title">
                  <h4>Expense Category</h4>
                  <h6>Manage Your Expense Category</h6>
                </div>
              </div>
              <ul className="table-top-head">
                <li>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderCollapseTooltip}
                  >
                    <Link
                      data-bs-toggle="tooltip"
                      data-bs-placement="top"
                      id="collapse-header"
                      className={data ? "active" : ""}
                      onClick={() => dispatch(setToogleHeader(!data))}
                    >
                      <ChevronUp />
                    </Link>
                  </OverlayTrigger>
                </li>
              </ul>
              <div className="page-btn">
                {/* <Link to="#" className="btn btn-added" onClick={handleAdd}>
                  <PlusCircle className="me-2" />
                  Add Expense Category
                </Link> */}

                {permissions.create && (
                  <Link to="#" className="btn btn-added" onClick={handleAdd}>
                    <PlusCircle className="me-2" />
                    Add Expense Category
                  </Link>
                )}
              </div>
            </div>

            <div className="card table-list-card">
              <div className="card-body">
                <div className="table-top">
                  <div className="search-set">
                    <div className="search-input">
                      <input
                        type="text"
                        placeholder="Search by category name and description"
                        className="form-control form-control-sm formsearch"
                        onChange={handleSearchExpenseCategory}
                      />
                      <Link to className="btn btn-searchset">
                        <i data-feather="search" className="feather-search" />
                      </Link>
                    </div>
                  </div>
                </div>
                <div className="table-responsive">
                  <Table
                    columns={columns}
                    dataSource={displayCategories}
                    rowKey={(record) => record.id}
                    rowSelection={null}
                    pagination={false}
                  />
                </div>

                {totalCount > 0 && (
                  <div className="custom-pagination d-flex justify-content-between align-items-center mt-4 mb-4">
                    <div className="pagination-left">
                      Showing {startItem} - {endItem} of {totalCount}
                    </div>
                    <div className="pagination-right d-flex align-items-center">
                      <button
                        className="btn btn-sm btn-outline-secondary me-2"
                        disabled={currentPage === 1}
                        onClick={() => changePage(currentPage - 1)}
                      >
                        <LeftOutlined size={12} />
                      </button>
                      {pagesToShow.map((page, index) => (
                        <span key={index} className="me-2">
                          {page === "..." ? (
                            <span className="pagination-ellipsis">...</span>
                          ) : (
                            <button
                              className={`btn btn-sm ${
                                page === currentPage
                                  ? "btn-primary"
                                  : "btn-outline-secondary"
                              }`}
                              onClick={() => changePage(page)}
                            >
                              {page}
                            </button>
                          )}
                        </span>
                      ))}
                      <button
                        className="btn btn-sm btn-outline-secondary ms-2"
                        disabled={currentPage === totalPages}
                        onClick={() => changePage(currentPage + 1)}
                      >
                        <RightOutlined size={12} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {isModalOpen && <div className="modal-backdrop fade show" />}
        <div
          className={`modal fade ${isModalOpen ? "show" : ""}`}
          id="add-units"
          style={{ display: isModalOpen ? "block" : "none" }}
        >
          <div className="modal-dialog modal-dialog-centered custom-modal-two">
            <div className="modal-content">
              <div className="page-wrapper-new p-0">
                <div className="content">
                  <div className="modal-header border-0 custom-modal-header d-flex justify-content-between align-items-center">
                    <div className="page-title">
                      <h4>
                        {isEditMode
                          ? "Update Expense Category"
                          : "Add Expense Category"}
                      </h4>
                    </div>
                    <button
                      type="button"
                      className="close"
                      aria-label="Close"
                      onClick={() => {
                        setIsModalOpen(false);
                        setIsEditMode(false);
                        setEditCategoryId(null);
                        setFormData({
                          name: "",
                          description: "",
                          status: "active",
                        });
                      }}
                    >
                      <span aria-hidden="true">×</span>
                    </button>
                  </div>
                  <div className="modal-body custom-modal-body">
                    <form onSubmit={handleSubmit}>
                      <div className="row">
                        <div className="col-lg-12">
                          <div className="mb-3">
                            <label className="form-label">Expense Name</label>
                            <input
                              type="text"
                              className={`form-control ${
                                errors.name ? "is-invalid" : ""
                              }`}
                              name="name"
                              value={formData.name}
                              onChange={handleInputChange}
                              placeholder="Enter expense category name"
                            />
                          </div>
                        </div>
                        <div className="col-md-12 mb-3">
                          <div className="edit-add card">
                            <div className="edit-add">
                              <label className="form-label">Description</label>
                            </div>
                            <div className="card-body-list input-blocks mb-0">
                              <textarea
                                className="form-control"
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                placeholder="Enter detail"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Status</label>
                          <select
                            className={`form-select ${
                              errors.status ? "is-invalid" : ""
                            }`}
                            name="status"
                            value={formData.status}
                            onChange={handleInputChange}
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                          {errors.status && (
                            <div className="invalid-feedback">
                              {errors.status}
                            </div>
                          )}
                        </div>
                      </div>
                      {/* <div className="modal-footer-btn">
                        <button
                          type="button"
                          className="btn btn-cancel me-2"
                          onClick={() => {
                            setIsModalOpen(false);
                            setIsEditMode(false);
                            setEditCategoryId(null);
                            setFormData({
                              name: "",
                              description: "",
                              status: "active",
                            });
                          }}
                        >
                          Cancel
                        </button>
                        <button type="submit" className="btn btn-submit">
                          {isEditMode ? "Update" : "Submit"}
                        </button>
                      </div> */}

                      <div className="modal-footer-btn">
                        <button
                          type="button"
                          className="btn btn-cancel me-2"
                          onClick={() => {
                            setIsModalOpen(false);
                            setIsEditMode(false);
                            setEditCategoryId(null);
                            setFormData({
                              name: "",
                              description: "",
                              status: "active",
                            });
                          }}
                        >
                          Cancel
                        </button>

                        {/* ✅ Show Submit only when Add mode + create permission */}
                        {!isEditMode && permissions.create && (
                          <button type="submit" className="btn btn-submit">
                            Submit
                          </button>
                        )}

                        {/* ✅ Show Update only when Edit mode + update permission */}
                        {isEditMode && permissions.update && (
                          <button type="submit" className="btn btn-submit">
                            Update
                          </button>
                        )}
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ExpenseCategory;
